/**
 * FeatureDiscovery.jsx — shared feature card, the homepage "New to explore"
 * band, and the header feature search. Additive discovery, no popups.
 */
import React, { useState, useMemo, useEffect, useRef } from "react";
import { filterCatalog, TYPES } from "@/lib/catalog";
import { Link, useNavigate } from "react-router-dom";
import { Search, X, Sparkles, Clock } from "lucide-react";
import { getActiveChild } from "@/lib/children";
import * as FR from "@/lib/featureRegistry";
import { track } from "@/lib/track";
const TYPE_LABEL = Object.fromEntries(TYPES.map(([t, l]) => [t, l]));

const TIER_BADGE = { seedlings: "Ages 4–6", explorers: "Ages 7–10", pioneers: "Ages 11–15" };
function tierLabel(f) {
  const t = f.tiers || [];
  if (t.length >= 3) return "All ages";
  return t.map((x) => TIER_BADGE[x]?.replace("Ages ", "")).join(" · ");
}
function currentTier() { try { return getActiveChild()?.tier || null; } catch { return null; } }

export function FeatureCard({ f, onOpen, compact }) {
  const nav = useNavigate();
  const older = f.fit === "older";
  const go = () => {
    if (older) return;
    FR.markOpened(f.key);
    track("feature_card_clicked", { feature: f.key });
    onOpen?.(f.key);
    nav(f.path);
  };
  return (
    <button onClick={go} disabled={older} data-testid={`feature-card-${f.key}`}
      className={`text-left rounded-2xl border-[3px] border-slate-900 bg-white p-3 shadow-[0_3px_0_0_rgba(15,23,42,1)] transition-transform ${older ? "opacity-70" : "hover:-translate-y-0.5"} ${compact ? "w-[220px] shrink-0 sm:w-auto sm:shrink min-w-0" : ""}`}>
      <div className="flex items-center gap-2">
        <span className="text-2xl" aria-hidden>{f.emoji}</span>
        <span className="font-heading font-black text-slate-900 leading-tight">{f.name}</span>
        {FR.isNew(f) && !older && <span className="ml-auto text-[9px] font-black uppercase bg-rose-500 text-white rounded-full px-1.5 py-0.5">New</span>}
        {f.opened && <span className="ml-auto text-[9px] font-black uppercase bg-emerald-100 text-emerald-700 rounded-full px-1.5 py-0.5">Started</span>}
      </div>
      <p className="text-xs font-semibold text-slate-600 mt-1">{f.desc}</p>
      <div className="mt-2 flex items-center gap-2 text-[10px] font-bold text-slate-400 flex-wrap">
        <span className="inline-flex items-center gap-0.5"><Clock size={11} /> {f.minutes}</span>
        <span className="rounded-full bg-slate-100 px-1.5 py-0.5">{tierLabel(f)}</span>
      </div>
      <p className="text-[10px] text-indigo-600 font-bold mt-1">Builds: {f.builds}</p>
      {older && <p className="text-[10px] text-amber-600 font-bold mt-1">✨ For older explorers</p>}
    </button>
  );
}

export function NewToExplore() {
  const tier = currentTier();
  const band = useMemo(() => FR.bandFeatures(tier), [tier]);
  useEffect(() => { if (band.mode !== "empty") track("homepage_new_band_viewed", { mode: band.mode }); }, [band.mode]);
  if (band.mode === "empty") return null;
  return (
    <section className="mb-6 md:mb-8" data-testid="home-new-to-explore">
      <div className="flex items-center gap-2 mb-2">
        <Sparkles size={18} className="text-amber-500" />
        <h2 className="font-heading text-lg font-black text-slate-900">{band.mode === "jump" ? "Jump back in" : "New to explore"}</h2>
      </div>
      <div className="flex gap-3 overflow-x-auto pb-2 sm:grid sm:grid-cols-2 sm:overflow-visible lg:grid-cols-4">
        {band.items.map((f) => (
          <FeatureCard key={f.key} f={f} compact onOpen={() => track("homepage_new_band_clicked", { feature: f.key })} />
        ))}
      </div>
    </section>
  );
}

export function FeatureSearch() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const inputRef = useRef(null);
  // Header search covers the WHOLE catalogue (games, lessons, countries, stories, quests…) — features first, then everything else.
  const results = useMemo(() => {
    const feats = FR.searchFeatures(q);
    const seen = new Set(feats.map((f) => f.path));
    const rest = q.trim() ? filterCatalog({ q }).filter((c) => !seen.has(c.path)).slice(0, 12).map((c) => ({ ...c, key: `cat-${c.key}`, minutes: `${c.minutes} min`, builds: TYPE_LABEL[c.type] || c.type, tiers: c.tiers })) : [];
    return [...feats, ...rest];
  }, [q]);
  const tier = currentTier();
  useEffect(() => { if (open) { setTimeout(() => inputRef.current?.focus(), 50); track("search_used", {}); } }, [open]);
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="feature-search-btn" aria-label="Search activities"
        className="shrink-0 inline-flex items-center justify-center h-10 w-10 rounded-full border-[3px] border-slate-900 bg-white shadow-[0_3px_0_0_rgba(15,23,42,1)] hover:translate-y-[1px] transition-all">
        <Search size={18} strokeWidth={3} />
      </button>
      {open && (
        <div className="fixed inset-0 z-[60] bg-slate-900/40 backdrop-blur-sm flex items-start justify-center pt-24 px-4" data-testid="feature-search-overlay" onClick={() => setOpen(false)}>
          <div className="w-full max-w-lg rounded-3xl border-4 border-slate-900 bg-white p-4 shadow-[8px_8px_0_0_rgba(15,23,42,1)]" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-3">
              <Search size={18} className="text-slate-400" />
              <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} data-testid="feature-search-input"
                placeholder="Find anything — try 'Kenya', 'fractions', 'story' or 'match pairs'"
                className="flex-1 outline-none font-semibold text-slate-800" />
              <button onClick={() => setOpen(false)} aria-label="Close" className="text-slate-400 hover:text-slate-800"><X /></button>
            </div>
            <div className="space-y-2 max-h-[50vh] overflow-y-auto" data-testid="feature-search-results">
              {q && <Link to={`/everything?q=${encodeURIComponent(q)}`} onClick={() => setOpen(false)} data-testid="feature-search-everything" className="block text-xs font-bold underline text-slate-700">See all results in Explore everything →</Link>}
              {q && results.length === 0 && <p className="text-sm text-slate-400 text-center py-4">No matches — try another word.</p>}
              {(q ? results : FR.FEATURES).map((f) => (
                <FeatureCard key={f.key} f={{ ...f, opened: FR.isOpened(f.key), fit: FR.suitability(f, tier) }}
                  onOpen={() => { track("search_result_clicked", { feature: f.key }); setOpen(false); }} />
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
